<template>
  <div>
    <EditorSection title="Projetos da campanha" description="Alterações nesta seção são gravadas na hora (adicionar, remover e apoiadores).">
      <template slot="aside">
        <span class="ed-status-pill ed-status-pill--on">{{ total }} projeto(s)</span>
      </template>

      <div class="ed-grid mb-2">
        <div>
          <v-text-field
            v-model="search"
            prepend-inner-icon="search"
            label="Buscar por projeto ou instituição"
            clearable
            hide-details
          ></v-text-field>
        </div>
      </div>

      <div class="pj-list" :class="{ 'pj-list--loading': loading }">
        <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="ma-0 pj-list__progress"></v-progress-linear>
        <div v-if="!items.length && !loading" class="ed-empty">
          {{ search ? "Nenhum projeto encontrado para esta busca." : "Nenhum projeto vinculado a esta campanha." }}
        </div>
        <article v-for="project in items" :key="project.projectId" class="pj-row">
          <EditorProjectThumb :project="project" />

          <div class="pj-body">
            <div class="pj-title">
              <span class="pj-name">{{ project.shortName || project.fullName }}</span>
              <span class="pj-tag">{{ projectTypeName(project.projectTypeId) }}</span>
              <span v-if="!project.captureStatus" class="pj-tag pj-tag--danger">Captação desligada</span>
            </div>
            <div class="pj-meta">
              <span><i class="fal fa-building mr-1"></i>{{ project.institutionName || "Sem instituição" }}</span>
              <span class="pj-meta__id">#{{ project.projectId }}</span>
            </div>
            <p v-if="project.objective" class="pj-objective" :title="project.objective">{{ project.objective }}</p>
            <div class="pj-supporters">
              <span class="pj-supporters__label">Apoiadores</span>
              <span v-if="!project.supporters.length" class="ed-help">nenhum</span>
              <span v-for="supporter in project.supporters" :key="supporter.companyId" class="pj-chip">{{ supporter.name }}</span>
            </div>
          </div>

          <div class="pj-actions">
            <v-btn small flat icon title="Editar apoiadores" @click="supportersProject = project"><i class="fal fa-users"></i></v-btn>
            <v-btn small flat icon title="Remover da campanha" class="pj-actions__remove" @click="removeProject(project)">
              <i class="fal fa-trash"></i>
            </v-btn>
          </div>
        </article>
      </div>

      <div v-if="total" class="pj-footer">
        <div class="pj-footer__size">
          <span class="ed-help">Por página</span>
          <select :value="pagination.rowsPerPage" class="pj-footer__select" @change="setRowsPerPage(Number($event.target.value))">
            <option v-for="size in rowsPerPageItems" :key="size" :value="size">{{ size }}</option>
          </select>
        </div>
        <span class="ed-help">{{ rangeText }}</span>
        <v-pagination
          v-if="pageCount > 1"
          :value="pagination.page"
          :length="pageCount"
          :total-visible="7"
          circle
          @input="setPage"
        ></v-pagination>
      </div>
    </EditorSection>

    <EditorSection title="Adicionar projetos" description="Projetos com captação ligada e do mesmo tipo da campanha.">
      <ProjectCandidates :campaign-id="campaignId" @added="fetch" />
    </EditorSection>

    <EditorSupportersDialog
      v-if="supportersProject"
      :campaign-id="campaignId"
      :project="supportersProject"
      @close="supportersProject = null"
      @saved="onSupportersSaved"
    />
  </div>
</template>

<script>
import CampaignEditorService from "@/scripts/services/campaignEditor.service";
import EditorSection from "../EditorSection.vue";
import EditorProjectThumb from "../EditorProjectThumb.vue";
import ProjectCandidates from "../ProjectCandidates.vue";
import EditorSupportersDialog from "../EditorSupportersDialog.vue";
import { projectTypeName } from "../editorLabels";

const service = new CampaignEditorService();
const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: "ProjectsTab",
  components: { EditorSection, EditorProjectThumb, ProjectCandidates, EditorSupportersDialog },
  props: {
    campaignId: { type: [Number, String], required: true },
  },
  data() {
    return {
      items: [],
      total: 0,
      loading: false,
      search: "",
      searchTimer: null,
      pagination: { page: 1, rowsPerPage: 20, sortBy: null, descending: false },
      rowsPerPageItems: Object.freeze([10, 20, 50, 100]),
      supportersProject: null,
      requestSeq: 0,
    };
  },
  computed: {
    pagingKey() {
      return `${this.pagination.page}-${this.pagination.rowsPerPage}`;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pagination.rowsPerPage));
    },
    rangeText() {
      const first = (this.pagination.page - 1) * this.pagination.rowsPerPage + 1;
      const last = Math.min(this.total, first + this.items.length - 1);
      return `${first}–${last} de ${this.total}`;
    },
  },
  watch: {
    // Só página e tamanho disparam nova consulta (a tabela também emite ao montar/ordenar).
    pagingKey() {
      this.fetch();
    },
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.pagination.page !== 1) this.pagination = Object.assign({}, this.pagination, { page: 1 });
        else this.fetch();
      }, SEARCH_DEBOUNCE_MS);
    },
  },
  mounted() {
    this.fetch();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    projectTypeName,
    fetch() {
      // Ignora respostas fora de ordem (busca digitada rápido).
      const seq = ++this.requestSeq;
      this.loading = true;
      return service
        .listProjects(this.campaignId, this.pagination.page, this.pagination.rowsPerPage, this.search)
        .then((page) => {
          if (seq !== this.requestSeq) return;
          this.items = Object.freeze(page.items || []);
          this.total = page.total || 0;
        })
        .catch(() => {})
        .finally(() => {
          if (seq === this.requestSeq) this.loading = false;
        });
    },
    removeProject(project) {
      const name = project.shortName || project.fullName;
      if (!window.confirm(`Você tem certeza que deseja remover o projeto "${name}" desta campanha? Os apoiadores dele também serão removidos.`)) return;
      service.removeProject(this.campaignId, project.projectId).then(() => {
        this.$store.commit("SET_SNACKBAR_SUCCESS_MESSAGE", "Projeto removido da campanha.");
        this.fetch();
      }).catch(() => {});
    },
    onSupportersSaved() {
      this.supportersProject = null;
      this.fetch();
    },
    setPage(page) {
      this.pagination = Object.assign({}, this.pagination, { page });
    },
    setRowsPerPage(rowsPerPage) {
      this.pagination = Object.assign({}, this.pagination, { rowsPerPage, page: 1 });
    },
  },
};
</script>

<style scoped>
.pj-list {
  position: relative;
  border: 1px solid var(--ced-border, #e3e5ec);
  border-radius: 8px;
  min-height: 64px;
}
.pj-list--loading .pj-row {
  opacity: 0.6;
}
.pj-list__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
.pj-list .ed-empty {
  border: 0;
}
.pj-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 12px 14px 14px;
  transition: background-color 0.15s;
}
.pj-row + .pj-row {
  border-top: 1px solid var(--ced-border, #e3e5ec);
}
.pj-row:hover {
  background: #fafbfc;
}
.pj-body {
  flex: 1;
  min-width: 0;
}
.pj-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
}
.pj-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--ced-text, #4c4d4f);
  line-height: 1.3;
}
.pj-tag {
  padding: 1px 8px;
  border-radius: 999px;
  background: #e6f6f7;
  color: #02858b;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.pj-tag--danger {
  background: #fdecec;
  color: #c62828;
}
.pj-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 4px;
  font-size: 13px;
  color: var(--ced-muted, #80849a);
}
.pj-meta__id {
  color: #a3a7b8;
}
.pj-objective {
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #5f6377;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 90ch;
}
.pj-supporters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}
.pj-supporters__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a3a7b8;
  margin-right: 2px;
}
.pj-chip {
  padding: 2px 10px;
  border-radius: 999px;
  background: #f1f2f5;
  color: var(--ced-text, #4c4d4f);
  font-size: 12px;
}
.pj-actions {
  flex: 0 0 auto;
  display: flex;
}
.pj-actions__remove:hover i {
  color: #c62828;
}
.pj-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-top: 12px;
}
.pj-footer__size {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.pj-footer__select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--ced-border, #e3e5ec);
  border-radius: 6px;
  background: #fff;
  color: var(--ced-text, #4c4d4f);
  font-size: 13px;
  -webkit-appearance: menulist;
  appearance: menulist;
  cursor: pointer;
}
@media (max-width: 600px) {
  .pj-row {
    flex-wrap: wrap;
  }
  .pj-actions {
    margin-left: auto;
  }
}
</style>
